import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  styled,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Switch,
  Alert,
  useMediaQuery
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import {
  selectSubscriptions,
  getPlans,
  getSubscription,
  updateSubscription,
  cancelSubscription,
  toggleAutoRenewal
} from '../../../../store/slices/subscriptionsSlice';
import { jsonData } from '../../../../db';

const StyledCard = styled(Card)(({ theme }) => ({
  textAlign: 'left',
  borderRadius: '5px',
  padding: theme.spacing(2),
  border: '1px solid #d7e1ff',
  position: 'relative',
  minHeight: '575px',
  minWidth: '250px',
  boxShadow: 'none',
  flex: 1
}));

const PlansContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: 16,
  flexWrap: 'wrap'
}));

const PriceTag = styled(Typography)(({ theme, selected }) => ({
  color: selected ? '#fff' : theme.palette.text.primary,
  fontSize: '2rem',
  fontWeight: 'bold'
}));

const FeatureList = styled('ul')({
  display: 'flex',
  flexDirection: 'column',
  listStyle: 'none',
  padding: 0,
  marginTop: '1rem',
  gap: 15
});

const checkIconStyle = {
  marginRight: 0.5,
  color: '#4CAF50',
  fontSize: '16px',
  borderRadius: '100%'
};

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const Subscription = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { subscription } = useSelector(selectSubscriptions);
  const isLargeScreen = useMediaQuery((theme) => theme.breakpoints.up('lg'));

  useEffect(() => {
    dispatch(getPlans());
  }, [dispatch]);

  const activePlan = subscription.data?.[0];
  const isTrialActive = subscription.isTrialActive;
  const trialDaysRemaining = subscription.trialDaysRemaining;
  const isPastDue = subscription.isPastDue;
  const autoRenew = subscription.autoRenew;
  const hasUsedTrial = activePlan?.has_used_trial;
  const accessUntil = subscription.accessUntil
    ? new Date(subscription.accessUntil)
        .toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric'
        })
        .replace(/ /g, ' ')
    : null;
  const trialEndDate =
    activePlan?.canceled_at && isTrialActive
      ? new Date(activePlan.canceled_at)
          .toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
          })
          .replace(/ /g, ' ')
      : null;
  const nextBillingDate = activePlan?.next_billing_date
    ? new Date(activePlan.next_billing_date)
        .toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric'
        })
        .replace(/ /g, ' ')
    : null;
  const isAccessActive = activePlan?.canceled_at
    ? new Date() < new Date(activePlan.canceled_at)
    : false;

  // const handleSubscribeNow = async () => {
  //   const body = { plan: 'comprehensive' };
  //   const result = await dispatch(createSubscription(body));
  //   if (result.meta.requestStatus === 'fulfilled') {
  //     await delay(1000); // 1-second delay
  //     dispatch(getSubscription());
  //   }
  // };

  const handleUpgrade = async () => {
    if (!activePlan?.subscription_id) return;
    const body = {
      subscription_id: activePlan.subscription_id,
      plan: 'comprehensive'
    };
    const result = await dispatch(updateSubscription(body));
    if (result.meta.requestStatus === 'fulfilled') {
      await delay(1000);
      dispatch(getSubscription());
    }
  };

  const handleCancel = async () => {
    const result = await dispatch(cancelSubscription());
    if (result.meta.requestStatus === 'fulfilled') {
      await delay(1000);
      dispatch(getSubscription());
    }
  };

  const handleToggleAutoRenew = async () => {
    const result = await dispatch(toggleAutoRenewal());
    if (result.meta.requestStatus === 'fulfilled') {
      await delay(1000);
      dispatch(getSubscription());
    }
  };

  const renderStatusText = () => {
    if (isPastDue) {
      return (
        <Box sx={{ mb: 2, maxWidth: isLargeScreen ? 300 : '100%' }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {t('Payment failed — update card')}
          </Alert>
        </Box>
      );
    }

    if (isTrialActive) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Typography>{t(`Free Week — ends ${trialEndDate}`)}</Typography>
          <Chip label={`${trialDaysRemaining} ${t('days')}`} color="primary" size="small" />
          <Button
            variant="contained"
            color="secondary"
            onClick={handleUpgrade}
            disabled={subscription.isLoading || !activePlan?.subscription_id}
            sx={{ ml: 2 }}>
            {t('Upgrade')}
          </Button>
        </Box>
      );
    }

    if (activePlan?.plan === 'comprehensive' && !isTrialActive) {
      if (!autoRenew && isAccessActive) {
        return <Typography sx={{ mb: 2 }}>{t(`Access until ${accessUntil}`)}</Typography>;
      }
      return (
        <Typography sx={{ mb: 2 }}>{t(`Advanced Search — renews ${nextBillingDate}`)}</Typography>
      );
    }

    if (activePlan?.plan === 'free' && hasUsedTrial) {
      if (isAccessActive) {
        return <Typography sx={{ mb: 2 }}>{t(`Access until ${accessUntil}`)}</Typography>;
      }
      return (
        <>
          <Alert severity="warning" sx={{ maxWidth: '100%', mb: 2 }}>
            {t('Your subscription has ended. Please subscribe to continue using premium features.')}
          </Alert>
          <Button
            variant="contained"
            color="primary"
            onClick={handleUpgrade}
            disabled={subscription.isLoading}
            sx={{ width: '100%', mb: 2 }}>
            {t('Subscribe Now')}
          </Button>
        </>
      );
    }

    return null;
  };

  return (
    <Box
      sx={{
        p: {
          lg: 3,
          md: 2,
          xs: 1.5
        },
        width: '100%'
      }}>
      <Box
        sx={{
          mb: 2,
          maxWidth: isLargeScreen ? 500 : '100%'
        }}>
        {renderStatusText()}

        {activePlan?.plan === 'comprehensive' && !isPastDue && !isTrialActive && (
          <>
            {activePlan?.status !== 'incomplete' && activePlan?.status !== 'trialing' && (
              <Box sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography>{t('Auto-renew')}</Typography>
                  <Switch
                    checked={autoRenew}
                    onChange={handleToggleAutoRenew}
                    disabled={subscription.isLoading}
                  />
                  <Chip
                    label={autoRenew ? t('ON') : t('OFF')}
                    color={autoRenew ? 'success' : 'default'}
                    size="small"
                  />
                </Box>
              </Box>
            )}
            {autoRenew && (
              <Button
                variant="outlined"
                onClick={handleCancel}
                disabled={subscription.isLoading}
                sx={{
                  mt: 1,
                  width: '100%',
                  color: 'red',
                  borderColor: 'red',
                  '&:hover': { backgroundColor: 'rgba(255,0,0,0.1)' }
                }}>
                {t('Cancel Subscription')}
              </Button>
            )}
          </>
        )}
      </Box>

      <PlansContainer>
        {jsonData.shortPlan.map((plan, index) => (
          <StyledCard
            sx={{
              backgroundColor: plan.selected ? '#3a5bc7' : '#e1e9ff',
              color: plan.selected ? '#fff' : '#000'
            }}>
            <CardContent>
              <Box sx={{ height: '200px', paddingBottom: '24px' }}>
                <Typography variant="h6" gutterBottom>
                  {t(plan.title)}
                  {activePlan?.plan === plan.plan && activePlan?.status !== 'trialing' && (
                    <Box
                      sx={{
                        width: 2,
                        height: 2,
                        borderRadius: '50%',
                        background: 'green'
                      }}></Box>
                  )}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <PriceTag selected={plan.selected}>{plan.price}</PriceTag>
                  {plan.plan !== 'free' && (
                    <Typography sx={{ py: 1, color: plan.selected ? 'white' : 'inherit' }}>
                      / {t('per month')}
                    </Typography>
                  )}
                </Box>
                <Typography
                  sx={{
                    fontSize: '12px',
                    maxWidth: isLargeScreen ? 170 : '100%',
                    color: plan.plan === 'free' ? '#000' : '#fff',
                    pt: plan.plan === 'free' ? 1 : 0
                  }}>
                  {t(plan.description)}
                </Typography>
              </Box>
              <Box
                sx={{
                  width: '100%',
                  height: '1px',
                  backgroundColor: plan.selected ? '#ffffff33' : '#20202033'
                }}
              />
              <Box sx={{ pt: 2 }}>
                <Typography
                  sx={{ py: 3, pt: 1, pb: '8px', color: plan.selected ? 'white' : 'inherit' }}>
                  {t('Key Features')}
                </Typography>
                <FeatureList>
                  {plan.features.map((feature, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        marginBottom: '12px',
                        gap: 1
                      }}>
                      <CheckCircleOutlineIcon
                        sx={{
                          ...checkIconStyle,
                          color: plan.selected ? 'white' : checkIconStyle.color,
                          backgroundColor: plan.selected
                            ? 'rgba(255, 255, 255, 0.5)'
                            : 'rgba(76, 175, 80, 0.1)'
                        }}
                      />
                      <Typography variant="body2" sx={{ fontWeight: 'light' }}>
                        {t(feature.title)}
                      </Typography>
                    </Box>
                  ))}
                </FeatureList>
              </Box>
              {plan.advantages?.length > 0 && (
                <>
                  <Box
                    sx={{
                      width: '100%',
                      height: '1px',
                      marginTop: '28px',
                      backgroundColor: plan.selected ? '#ffffff33' : '#20202033'
                    }}
                  />
                  <Typography sx={{ py: 3, color: plan.selected ? 'white' : 'inherit' }}>
                    {t('Advantages')}
                  </Typography>
                  {plan.advantages.map((advantage, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        marginBottom: '12px',
                        gap: 1
                      }}>
                      <CheckCircleOutlineIcon
                        sx={{
                          ...checkIconStyle,
                          color: plan.selected ? 'white' : checkIconStyle.color,
                          backgroundColor: plan.selected
                            ? 'rgba(255, 255, 255, 0.5)'
                            : 'rgba(76, 175, 80, 0.1)'
                        }}
                      />
                      <Typography variant="body2" sx={{ fontWeight: 'light' }}>
                        {t(advantage.title)}
                      </Typography>
                    </Box>
                  ))}
                </>
              )}

              {plan.plan === 'comprehensive' &&
                (activePlan?.plan !== 'comprehensive' || activePlan?.status === 'incomplete') &&
                !isTrialActive && (
                  <Button
                    variant="outlined"
                    onClick={handleUpgrade}
                    disabled={subscription.isLoading || !activePlan?.subscription_id}
                    sx={{
                      mt: 2,
                      width: 'calc(100% - 64px)',
                      position: 'absolute',
                      bottom: 40,
                      background: '#fff',
                      color: '#000',
                      borderColor: plan.selected ? '#fff' : '#848a98',
                      '&:hover': { backgroundColor: 'rgba(0,0,0,0.1)', color: '#fff' }
                    }}>
                    {t('Upgrade')}
                  </Button>
                )}
            </CardContent>
          </StyledCard>
        ))}
      </PlansContainer>
    </Box>
  );
};

export default Subscription;
